import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getCodeFromUrl,
  isUserAuthenticated,
  redirectToAuthPage
} from '../../utils/TokenManager';
import { fetchTokenUsingAuthCode } from '../../redux/Auth/AuthSlice';
import { AppDispatch, RootState } from '../../redux/Store';
import { PATHS } from '../../routes/Routes';
import LoaderPage from './LoaderPage';

/**
 * AutoLogin Component
 *
 * Handles the SSO authentication flow automatically by:
 * 1. Checking for existing authentication
 * 2. Processing auth codes from redirect URLs
 * 3. Managing token fetch operations
 * 4. Redirecting users based on authentication status
 *
 * This component acts as a middleware between the authentication service
 * and protected application routes.
 */
const AutoLogin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { accessTokenStatus } = useSelector((state: RootState) => state.auth);
  const isAuthenticated = isUserAuthenticated();
  const authCode = getCodeFromUrl();

  /**
   * Primary effect for handling the authentication flow
   * Runs when auth code, authentication status
   */
  useEffect(() => {
    handleAuthFlow();
  }, [authCode, isAuthenticated]);

  /**
   * Secondary effect for handling token status changes
   * Redirects user based on the success/failure of token acquisition
   */
  useEffect(() => {
    if (accessTokenStatus === 'failed') {
      redirectToAuthPage();
    }
    if (accessTokenStatus === 'succeeded') {
      navigate(PATHS.LANDING);
    }
  }, [accessTokenStatus, navigate]);

  /**
   * Manages the authentication logic flow:
   * - Validates existing tokens
   * - Processes authentication codes
   * - Handles redirection based on auth state
   * - Provides error handling with fallback to auth page
   */
  const handleAuthFlow = async (): Promise<void> => {
    try {
      if (authCode) {
        await dispatch(fetchTokenUsingAuthCode(authCode));
        return;
      }

      if (!isAuthenticated) {
        redirectToAuthPage();
        return;
      }
    } catch (error) {
      // Handle any unexpected errors by redirecting to auth page
      redirectToAuthPage();
    }
  };

  // Show loading indicator while token acquisition is in progress
  if (accessTokenStatus === 'loading') {
    return <LoaderPage />;;
  }

  if (isUserAuthenticated()) {
    navigate(PATHS.LANDING);
  }

  // Return null when the component is handling redirections
  return null;
};

export default AutoLogin;
